import { useEffect, useMemo, useState } from "react"
import { useRouter } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { ANALYTICS_EVENTS } from "@/features/analytics"
import CreditIcon from "@/assets/img/CreditsV4.webp"
import HeroBackground from "@/assets/img/live-chat/quiz/step7_background.webp"
import LeafDecor from "@/assets/img/live-chat/leaf_decor.svg?react"
import ArrowLeft from "@/assets/img/live-chat/quiz/arrow-left.svg?react"
import Star from "@/assets/svg/advisor-inner/star.svg?react"
import Verified from "@/assets/svg/advisor-inner/verified.svg?react"
import Dot from "@/assets/svg/advisors/Dot.svg?react"

import { advisorsList } from "../config/advisors"
import { advisorInnerCopy, advisorInnerFaqs } from "../config/advisor-inner"
import { useAdvisorActions } from "../hooks/use-advisor-actions"

import { AdvisorQuote } from "./advisor-quote"
import { AdvisorComment } from "./advisor-comment"
import { AdvisorTrustedGuide } from "./advisor-trusted-guide"
import { AdvisorFaqList } from "./advisor-faq"
import { AdvisorCarouselSection } from "./advisor-carousel-section"
import { CreditsPurchaseModal } from "./credits-purchase-modal"

interface AdvisorInnerPageProps {
  advisorId?: string
}

/** Advisor detail page (`/webapp/advisors/inner`). Ported from AdvisorsInner (V4). */
export function AdvisorInnerPage({ advisorId }: AdvisorInnerPageProps) {
  const router = useRouter()
  const { userMainSubscriptionId } = useChat()
  const { isPaywallOpen, closePaywall, handleChatWithAdvisor, handleAdvisorInner } =
    useAdvisorActions()

  const advisor = useMemo(
    () => advisorsList.find((a) => a.assistantId === advisorId) ?? advisorsList[0],
    [advisorId],
  )

  // Three "you may also like" advisors, rotated by the current advisor's position so
  // different profiles surface different picks (deterministic — keeps render pure).
  const recommended = useMemo(() => {
    const others = advisorsList.filter((a) => a.assistantId !== advisor.assistantId)
    const start = advisorsList.findIndex((a) => a.assistantId === advisor.assistantId)
    const offset = start < 0 ? 0 : start % others.length
    return [...others.slice(offset), ...others.slice(0, offset)].slice(0, 3)
  }, [advisor.assistantId])

  const [showHeader, setShowHeader] = useState(false)
  useEffect(() => {
    const onScroll = () => setShowHeader(window.scrollY > 32)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const goBack = () => router.history.back()
  const firstName = advisor.name.split(" ")[0]

  return (
    <div data-slot="advisor-inner" className="relative -mx-4 bg-dark-base pb-0 text-purple-light">
      {showHeader && (
        <div className="fixed left-1/2 top-0 z-[100] flex w-full max-w-app -translate-x-1/2 items-center border-t border-purple-light/10 bg-dark-base px-4 py-3">
          <button type="button" onClick={goBack} className="absolute left-4 [&_svg]:size-6">
            <ArrowLeft />
          </button>
          <h2 className="mx-auto font-display text-[22px] font-medium text-purple-light">
            {advisor.name}
          </h2>
        </div>
      )}

      <button type="button" onClick={goBack} className="absolute left-4 top-4 z-[1] [&_svg]:size-6">
        <ArrowLeft />
      </button>

      {/* Hero background */}
      <img
        src={HeroBackground}
        alt=""
        className="pointer-events-none absolute left-0 top-0 h-60 w-full object-cover"
      />

      {/* Avatar */}
      <div className="relative mx-auto flex justify-center pt-10">
        <div className="relative flex size-[166px] min-w-[166px] items-center justify-center rounded-full bg-gradient-to-br from-peach to-purple-accent">
          <img
            src={advisor.photo}
            alt={advisor.name}
            className="size-40 rounded-full border-4 border-dark-base object-cover"
          />
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-2xl border-4 border-dark-base bg-dark-base">
            <div className="flex items-center gap-1 rounded-2xl bg-green/20 px-2 py-1 font-body text-xs text-green [&_svg]:size-2">
              <Dot />
              Online
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-[1]">
        <div className="mx-auto mt-4 flex items-center justify-center gap-2 text-center [&_svg]:size-6">
          <h1 className="font-display text-2xl font-medium text-purple-light">{advisor.name}</h1>
          <Verified />
        </div>
        <p className="text-center font-body text-sm text-purple-light/70">{advisor.expertise}</p>

        {/* Stats */}
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <StatCard
            label={advisorInnerCopy.readings}
            value={advisor.reviews.replace(/[()]/g, "")}
          />
          <div className="flex w-[122px] flex-col items-center justify-center rounded-xl bg-purple-light/10 px-4 py-3">
            <span className="text-center font-body text-sm text-purple-light/70">
              {advisorInnerCopy.usersRating}
            </span>
            <div className="flex items-center gap-1 [&_svg]:size-5">
              <Star />
              <span className="mt-0.5 font-display text-base font-medium text-purple-light">
                {advisor.rating}
              </span>
            </div>
          </div>
          <StatCard label={advisorInnerCopy.practice} value={`${advisor.practiceYears} years`} />
        </div>

        <div className="relative z-[3] mx-4">
          <button
            type="button"
            onClick={() => handleChatWithAdvisor(advisor)}
            className="mt-4 flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-80"
          >
            Chat with {firstName}
          </button>

          {/* Areas of expertise */}
          <div className="mt-10">
            <h3 className="font-display text-2xl font-medium text-purple-light">
              {advisorInnerCopy.areasExpertise}
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="flex items-center gap-1 rounded-[19px] bg-gold/20 px-4 py-2 font-body text-base font-medium text-gold [&_svg]:h-5 [&_svg]:w-2 [&_svg:last-child]:-scale-x-100">
                <LeafDecor />
                {advisorInnerCopy.specialCategory}
                <LeafDecor />
              </span>
              {advisor.categories?.map((category) => (
                <span
                  key={category}
                  className="rounded-[23px] bg-purple-light/10 px-4 py-2 font-body text-base text-purple-light"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>

          {/* About me */}
          <div className="mt-10">
            <h3 className="font-display text-2xl font-medium text-purple-light">
              {advisorInnerCopy.aboutMe}
            </h3>
            <p className="mt-4 font-body text-base text-purple-light/70">{advisor.bio}</p>
            {advisor.quote && <AdvisorQuote photo={advisor.photo} quote={advisor.quote} />}
          </div>

          {/* Guarantee */}
          <div className="mt-10">
            <h3 className="font-display text-2xl font-medium text-purple-light">
              {advisorInnerCopy.guarantee.title}
            </h3>
            <div className="mt-4 flex items-center gap-4">
              <Star className="size-14 shrink-0" />
              <p className="font-body text-base text-purple-light/70">
                {advisorInnerCopy.guarantee.subtitle}
              </p>
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-10">
            <div className="mb-4 flex items-center gap-2 [&_svg]:size-8">
              <Star />
              <h3 className="font-display text-2xl font-medium text-purple-light">
                <span className="text-gold">{advisor.rating}</span>{" "}
                {`(${advisor.reviews.replace(/[()]/g, "")} ${advisorInnerCopy.comments})`}
              </h3>
            </div>
            <div className="-mr-4 flex gap-4 overflow-x-auto pr-4 [scrollbar-width:none]">
              {advisor.comments?.map((review) => (
                <div key={review.name} className="w-[320px] shrink-0">
                  <AdvisorComment name={review.name} text={review.comment} />
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-10">
            <h3 className="font-display text-2xl font-medium text-purple-light">
              {advisorInnerCopy.questions.title}
            </h3>
            <AdvisorFaqList faqs={advisorInnerFaqs} />
          </div>
        </div>

        <div className="mt-10">
          <AdvisorTrustedGuide advisor={advisor} onChat={handleChatWithAdvisor} />
        </div>

        <AdvisorCarouselSection
          title={advisorInnerCopy.mayLike.title}
          subtitle={advisorInnerCopy.mayLike.subtitle}
          advisors={recommended}
          chatEvent={ANALYTICS_EVENTS.CHAT_WITH_RECOMMENDED_ADVISOR}
          onCardClick={handleAdvisorInner}
          onChatClick={handleChatWithAdvisor}
        />

        {/* Sticky bottom CTA */}
        <div className="sticky bottom-0 left-0 z-[100] mt-8 flex items-center justify-between gap-6 border-t border-purple-light/10 bg-dark-base p-4">
          <div>
            <p className="font-display text-base font-medium text-purple-light">
              Chat with {advisor.name}
            </p>
            <div className="flex items-center gap-1">
              <img src={CreditIcon} alt="" className="size-5" />
              <span className="font-body text-sm text-purple-light/70">
                {advisorInnerCopy.bottomMenu.price}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleChatWithAdvisor(advisor)}
            className="flex h-11 w-full max-w-[161px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-80"
          >
            {advisorInnerCopy.bottomMenu.button}
          </button>
        </div>
      </div>

      <CreditsPurchaseModal
        isOpen={isPaywallOpen}
        onClose={closePaywall}
        userMainSubscriptionId={userMainSubscriptionId}
        variant="main-page"
      />
    </div>
  )
}

interface StatCardProps {
  label: string
  value: string
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="flex w-[122px] flex-col items-center justify-center rounded-xl bg-purple-light/10 px-4 py-3">
      <span className="text-center font-body text-sm text-purple-light/70">{label}</span>
      <span className="mt-0.5 font-display text-base font-medium text-purple-light">{value}</span>
    </div>
  )
}
